import React, { useEffect, useState } from "react";
import axiosInstance from "../../interceptors";
import MetricCard from "./MetricCard";

// Inline metric strip backed by GET /common/analytics/inline.
// Trends are server-computed and rendered from response.trends; no hardcoded
// JSX values. Cards for metrics where the backend returns null are rendered
// with an em-dash placeholder so the layout doesn't shift on fail.
//
// Tenant scoping: tenantId is sent as a queryparam, read from localStorage.
export default function InlineMetricBar({ context = "DASHBOARD", contextId, metrics: metricKeys }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ context });
    params.set("tenantId", localStorage.getItem("tenantId") || "1");
    if (contextId) params.set("contextId", contextId);
    if (metricKeys) params.set("metrics", metricKeys.join(","));
    axiosInstance
      .get(`/common/analytics/inline?${params.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setError(e?.message || e?.data?.error || "Couldn't load metrics");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [context, contextId, metricKeys?.join(",")]);

  if (loading) return <Skeleton />;
  if (error) {
    return (
      <div className="mb-4 px-3 py-2 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800">
        Couldn't load metrics — {error}.
      </div>
    );
  }
  if (!data) return null;

  const trends = data.trends || {};
  const fmtVal = (v, fallback = "—") => (v == null ? fallback : v);

  return (
    <div className="flex gap-3 mb-4">
      <MetricCard
        value={fmtVal(data.applications, "0")}
        label="Applications"
        trend={trends.applications}
        trendLabel="vs last 30 days"
      />
      <MetricCard
        value={fmtVal(data.conversion)}
        label="Conversion rate"
        unit={data.conversion == null ? null : "%"}
        trend={trends.conversion}
        color="#7B5AC4"
      />
      <MetricCard
        value={fmtVal(data.avgScore)}
        label="Avg AI score"
        trend={trends.avgScore}
        color="#10B981"
      />
      <MetricCard
        value={fmtVal(data.timeToHire)}
        label="Time to hire"
        unit={data.timeToHire == null ? null : "days"}
        trend={trends.timeToHire}
        trendLabel={trends.timeToHire == null ? null : (trends.timeToHire < 0 ? "faster" : "slower")}
        color="#F59E0B"
        invertTrendColor
      />
      <MetricCard
        value={fmtVal(data.responseRate)}
        label="Response rate"
        unit={data.responseRate == null ? null : "%"}
        trend={trends.responseRate}
      />
    </div>
  );
}

function Skeleton() {
  return (
    <div className="flex gap-3 mb-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex-1 min-w-0 bg-white border border-slate-200 rounded-lg p-3 animate-pulse">
          <div className="h-7 w-16 bg-slate-200 rounded mb-2" />
          <div className="h-3 w-24 bg-slate-200 rounded mb-1" />
          <div className="h-3 w-10 bg-slate-200 rounded" />
        </div>
      ))}
    </div>
  );
}
